import { DOCTOR_ROLE, isNurse } from '@/auth/roles';
import type { UserData } from '@/auth/auth-api';

/**
 * Restrict the create-appointment provider list to the logged-in user when
 * that user is themselves a provider (PROD-1024, F-SCH-132).
 *
 * Mirrors Flutter `create_appointment_widget.dart:471-475 / 2202-2210`
 * (`GetDoctorSlotsRespState`) and `select_doctor_widget.dart:112-114`:
 *   - `role === doctorRole` ("3")            → keep only the doctor's own row
 *                                              (`element.id == userData.id`)
 *   - `isNurse(user)` (staff + nurse tenant role) → keep only the nurse's linked
 *                                              practitioner
 *                                              (`element.id == userData.practitionerId`)
 *
 * Any other role (org-admin / staff / etc.) sees the full roster unchanged.
 *
 * Pure + non-mutating — uses `.filter()` (returns a new array); never sorts or
 * splices the input, so the upstream React Query cache array is never touched.
 */
export function filterProvidersForCurrentUser<T extends { id?: string | null }>(
  providers: T[],
  user:
    | Pick<UserData, 'role' | 'id' | 'tenantRoleId' | 'practitionerId'>
    | null
    | undefined,
): T[] {
  if (!user) return providers;

  // `role` / `tenantRoleId` are numeric-string IDs ("3" / "4") in the typed
  // UserData, but the backend/JWT could surface either as a number. Coerce to
  // string so the comparison can't silently fail and fall through to the full
  // roster (PR #469 review). `null`/`undefined` → '' (never matches a role id).
  const role = user.role == null ? '' : String(user.role);

  // Doctor → only their own row. `id` is the doctorSetupId (Flutter
  // `userData.id`), reliably present once the profile loads; a transient absent
  // id yields an empty list — matching Flutter `removeWhere(element.id != id)`.
  // We intentionally do NOT expose other providers during that load window.
  if (role === DOCTOR_ROLE) {
    return providers.filter((p) => p.id === user.id);
  }

  // Nurse → only their linked practitioner. If the nurse account is NOT linked
  // to a practitioner (practitionerId falsy), do NOT strand them with an empty
  // list (they'd be unable to book at all) — fall through to the full roster.
  // Deliberate divergence from Flutter (which would show empty); reviewer-
  // requested (PR #469 review). Unlike a doctor's transient missing id, an
  // unlinked nurse is a persistent data state, so empty here is a real trap.
  // Nurse-ness goes through the shared `isNurse()` gate rather than a local
  // `tenantRoleId === "4"` test. The local test had no `role` requirement, so it
  // disagreed with every other nurse gate in the app (all of which also require
  // `role === "2"`) and would nurse-filter a non-staff user who happened to
  // carry tenant role id "4". One gate, one definition — see `auth/roles.ts`.
  if (isNurse(user)) {
    return user.practitionerId
      ? providers.filter((p) => p.id === user.practitionerId)
      : providers;
  }

  return providers;
}
